// SPDX-License-Identifier: MPL-2.0
import { Timestamp, z } from './common.ts';

export const Health = z
  .object({
    status: z.enum(['ok', 'degraded']),
    checks: z.object({
      database: z.enum(['ok', 'error']),
      queue: z.enum(['ok', 'error', 'disabled']),
    }),
  })
  .openapi('Health');
export type Health = z.infer<typeof Health>;

export const RegistrationMode = z.enum(['first-user', 'invite', 'open', 'closed']).openapi({
  description:
    '`first-user`: nobody has an account yet and the first sign-up becomes administrator. ' +
    '`invite`: only invited people can sign up. `open`: anyone can sign up. `closed`: no new accounts, including invitations.',
});

export const Instance = z
  .object({
    name: z.string(),
    publicUrl: z.url(),
    setupRequired: z.boolean(),
    registration: RegistrationMode,
    auth: z.object({
      emailPassword: z.boolean(),
      google: z.boolean(),
      oidc: z.array(z.object({ id: z.string(), name: z.string() })).openapi({
        description: 'Sign in with `POST /api/auth/sign-in/social` and `provider: id`.',
      }),
      magicLink: z.boolean().openapi({
        description: 'Sign-in links by email. Needs SMTP, so it is false without it.',
      }),
      email: z.boolean().openapi({
        description: 'The instance can send email (invitations, sign-in links).',
      }),
    }),
    releaseNews: z.boolean().optional().openapi({
      description: 'Release news available: SMTP configured and administrator switch enabled.',
    }),
    collabPath: z.string().openapi({ example: '/collab' }),
    media: z
      .object({
        videoPosters: z.boolean().openapi({
          description: 'The worker has ffmpeg: videos get poster thumbnails and a duration.',
        }),
        rawPreviews: z.boolean().openapi({
          description: 'The worker can decode camera RAW files into thumbnails.',
        }),
        pdfPreviews: z.boolean().openapi({
          description:
            'The worker renders page 1 of PDFs into thumbnails and reads their page count ' +
            '(poppler).',
        }),
        videoProxies: z.boolean().openapi({
          description:
            'The worker makes playable MP4 copies of videos browsers cannot play ' +
            '(`Asset.proxy`): ffmpeg is there and VIDEO_PROXIES is not `false`.',
        }),
      })
      .openapi({ description: 'Media tools available on this server (image tag `-media`).' }),
    maps: z
      .object({
        enabled: z.boolean().openapi({
          description:
            'Map cards and GPX files get a map picture, composited by the server from its ' +
            'tile server (MAPS, MAP_TILE_URL). False: they show only the address and link.',
        }),
        attribution: z.string().max(200).openapi({
          description: 'Credit of the map data to show on every map picture.',
        }),
        satelliteAttribution: z.string().max(200).optional().openapi({
          description:
            'Credit of the optional satellite source; absent when no source is configured.',
        }),
        geocoding: z.boolean().openapi({
          description: 'Addresses can be looked up (MAP_GEOCODER_URL); off by default.',
        }),
      })
      .openapi({ description: 'Maps of this server (decision 0053).' }),
    limits: z.object({
      uploadMaxBytes: z
        .int()
        .min(0)
        .openapi({ description: 'Largest file an upload may have; 0 means no limit.' }),
    }),
    serverTime: Timestamp,
  })
  .openapi('Instance');
export type Instance = z.infer<typeof Instance>;

export const DiagnosticId = z
  .enum([
    'public_url_https',
    'public_url_matches_request',
    'proxy_trusted',
    'database',
    'queue',
    'data_dir_writable',
    'storage',
    'video_encoding',
  ])
  .openapi({
    description:
      '`public_url_https`: PUBLIC_URL uses HTTPS (warning otherwise, except on localhost). ' +
      '`public_url_matches_request`: the scheme and host the server sees, after the proxy ' +
      'headers, match PUBLIC_URL (error otherwise: secure cookies break). `proxy_trusted`: a ' +
      'request carrying X-Forwarded-For comes from a peer that TRUST_PROXY accepts. `database`, ' +
      '`queue`: as in Health. `data_dir_writable`: DATA_DIR accepts writes. `storage`: a test ' +
      'file can be written to and removed from the configured storage. `video_encoding`: the ' +
      'encoder of playable video copies the last worker to start chose (`actual`), a warning ' +
      'when the hardware encoder VIDEO_PROXY_HWACCEL names did not work; absent without a ' +
      'worker with ffmpeg.',
  });
export type DiagnosticId = z.infer<typeof DiagnosticId>;

export const DiagnosticCheck = z
  .object({
    id: DiagnosticId,
    status: z.enum(['ok', 'warning', 'error']),
    expected: z.string().nullable().openapi({
      description: 'What the check wanted. Always null while the instance has no accounts.',
    }),
    actual: z.string().nullable().openapi({
      description: 'What the check found. Always null while the instance has no accounts.',
    }),
  })
  .openapi('DiagnosticCheck');
export type DiagnosticCheck = z.infer<typeof DiagnosticCheck>;

/**
 * Installation self-check. Texts are not part of the payload: clients translate `id` and
 * `status` and show `expected` and `actual` as they are.
 */
export const Diagnostics = z
  .object({
    version: z.string(),
    checkedAt: Timestamp,
    request: z
      .object({
        protocol: z.enum(['http', 'https']),
        host: z.string(),
        clientIp: z.string(),
        peerAddress: z.string().openapi({ description: 'Address of the TCP peer: the proxy.' }),
        forwardedFor: z.string().nullable().openapi({ description: 'Raw X-Forwarded-For.' }),
        proxyTrusted: z.boolean(),
      })
      .openapi({ description: 'The request of the caller, as the server sees it.' }),
    checks: z.array(DiagnosticCheck),
  })
  .openapi('Diagnostics');
export type Diagnostics = z.infer<typeof Diagnostics>;

/** Software identity is available only to signed-in users. */
export const InstanceVersion = z.object({ version: z.string() }).openapi('InstanceVersion');
export type InstanceVersion = z.infer<typeof InstanceVersion>;
